import React, { useState, useEffect } from 'react';

interface ItemViewPublicProps {
  isOpen: boolean;
  onClose: () => void;
  onSave?: (data: ItemData) => void;
  itemId?: number;
  itemData?: ItemData;
  mode?: 'view' | 'edit';
  // Yeni props - response'dan gelen veriler
  itemFromResponse?: {
    item?: {
      id?: number;
      itemName?: string;
      description?: string;
      itemType?: string;
      categoryName?: string;
      unit?: string;
      vatRate?: number;
      createdAt?: string;
      itemImages?: Array<{ imageUrl?: string }>;
    };
  };
}

interface ItemData {
  id: string;
  title: string;
  description: string;
  type: string;
  category: string;
  unit: string;
  vatRate: number;
  requestedDate: string;
  images: string[];
}

const ItemViewPublic: React.FC<ItemViewPublicProps> = ({
  isOpen,
  onClose,
  onSave,
  itemId,
  itemData,
  mode = 'view',
  itemFromResponse
}) => {
  const [isEditing, setIsEditing] = useState(mode === 'edit');
  const [currentSlide, setCurrentSlide] = useState(0);
  const [formData, setFormData] = useState<ItemData>(itemData || {
    id: '',
    title: '',
    description: '',
    type: '',
    category: '',
    unit: '',
    vatRate: 0,
    requestedDate: '',
    images: []
  });

  // Set form data when modal opens
  useEffect(() => {
    if (isOpen) {
      if (itemFromResponse?.item) {
        // Use data from response (no API call needed)
        const item = itemFromResponse.item;
        const images = item.itemImages?.map(img => img.imageUrl || '').filter(url => url) || [];
        
        setFormData({
          id: item.id?.toString() || '',
          title: item.itemName || '',
          description: item.description || '',
          type: item.itemType || '',
          category: item.categoryName || '',
          unit: item.unit || '',
          vatRate: item.vatRate || 0,
          requestedDate: item.createdAt || '',
          images
        });
      } else if (itemData) {
        // Use provided itemData
        setFormData(itemData);
      } else if (itemId) {
        // Use default data if no itemData provided
        setFormData({
          id: itemId.toString(),
          title: 'Ürün Adı',
          description: 'Ürün açıklaması burada görünecek',
          type: '',
          category: '',
          unit: '',
          vatRate: 0,
          requestedDate: '',
          images: []
        });
      }
    }
  }, [isOpen, itemId, itemData, itemFromResponse]);

  const handleInputChange = (field: keyof ItemData, value: string | number) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = () => {
    if (onSave) {
      onSave(formData);
    }
    setIsEditing(false);
  };

  const handleCancel = () => {
    if (itemData) {
      setFormData(itemData);
    }
    setIsEditing(false);
    onClose();
  };

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % formData.images.length);
  };

  const previousSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + formData.images.length) % formData.images.length);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto transition-colors duration-300">
        
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Ürün Genel Bilgileri</h2>
            <p className="text-gray-500 dark:text-gray-400 text-sm mt-1">Talep No: #{formData.id || '-'}</p>
          </div>
          <button
            onClick={handleCancel}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            <i className="fas fa-times text-xl"></i>
          </button>
        </div>

        {/* Main Content */}
        <div className="p-6">
          {/* Project Images and Details */}
          <div className="flex gap-6">
            {/* Project Images */}
            <div className="w-1/2">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">Ürün Görselleri</h3>
              <div className="relative overflow-hidden bg-gray-100 dark:bg-gray-700 rounded-lg aspect-square">
                {/* Navigation Buttons - Always visible */}
                <button 
                  onClick={previousSlide}
                  disabled={formData.images.length <= 1}
                  className="absolute left-2 top-1/2 z-10 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 p-2 rounded-full shadow-lg hover:bg-gray-50 dark:hover:bg-gray-700 hover:shadow-xl transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ transform: 'translateY(-50%)' }}
                >
                  <i className="fas fa-chevron-left text-sm"></i>
                </button>
                
                <button 
                  onClick={nextSlide}
                  disabled={formData.images.length <= 1}
                  className="absolute right-2 top-1/2 z-10 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 p-2 rounded-full shadow-lg hover:bg-gray-50 dark:hover:bg-gray-700 hover:shadow-xl transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ transform: 'translateY(-50%)' }}
                >
                  <i className="fas fa-chevron-right text-sm"></i>
                </button>
                
                {formData.images.length > 0 ? (
                  <div 
                    className="flex transition-transform duration-300 ease-in-out h-full"
                    style={{ transform: `translateX(-${currentSlide * 100}%)` }}
                  >
                    {formData.images.map((image, index) => (
                      <div key={index} className="min-w-full flex items-center justify-center">
                        <img 
                          className="w-full h-full object-cover" 
                          src={image} 
                          alt={`Ürün görseli ${index + 1}`}
                        />
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="flex items-center justify-center h-full">
                    <div className="text-center">
                      <i className="fas fa-mountain text-4xl text-gray-400 dark:text-gray-500 mb-3"></i>
                      <p className="text-gray-500 dark:text-gray-400 text-sm">Herhangi bir resim bulunmamakta</p>
                    </div>
                  </div>
                )}
              </div>
              
              {/* Slide Indicators - Only show when there are images */}
              {formData.images.length > 0 && (
                <div className="flex justify-center items-center mt-3">
                  <div className="flex space-x-1">
                    {formData.images.map((_, index) => (
                      <div
                        key={index}
                        className={`w-1.5 h-1.5 rounded-full ${
                          index === currentSlide ? 'bg-blue-600 dark:bg-blue-400' : 'bg-gray-300 dark:bg-gray-600'
                        }`}
                      ></div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Project Details - Right Column */}
            <div className="w-1/2 flex flex-col">
              {/* First Project Details */}
              <div className="h-full flex flex-col">
                <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">Ürün Detayları</h3>
                <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 flex-1 flex flex-col gap-4">
                  {/* Project Title */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Ürün Başlığı</label>
                    {isEditing ? (
                      <input
                        type="text"
                        value={formData.title}
                        onChange={(e) => handleInputChange('title', e.target.value)}
                        onFocus={(e) => e.target.select()}
                        className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        placeholder="Ürün başlığını girin"
                      />
                    ) : (
                      <div className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600">
                        <p className="text-gray-700 dark:text-gray-300">{formData.title || '-'}</p>
                      </div>
                    )}
                  </div>

                  {/* Project Description */}
                  <div className="flex-1 flex flex-col">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Ürün Açıklaması</label>
                    {isEditing ? (
                      <textarea
                        value={formData.description}
                        onChange={(e) => handleInputChange('description', e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white resize-none flex-1"
                        placeholder="Ürün açıklamasını girin"
                      />
                    ) : (
                      <div className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600 flex-1">
                        <p className="text-gray-700 dark:text-gray-300">{formData.description || '-'}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

        </div>

        {/* Footer */}
        <div className="flex justify-end space-x-3 p-6 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={handleCancel}
            className="px-6 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors font-medium"
          >
            Kapat
          </button>
          {isEditing && (
            <button
              onClick={handleSave}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
            >
              <i className="fas fa-save mr-2"></i>
              Kaydet
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ItemViewPublic;
